import { FormattedMessage, useIntl } from 'react-intl';
import { useFormContext } from 'react-hook-form';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import { useShiftDrawerTalents } from '@libs/store/shifts';
import { FormAutocompleteMultiple, FormAutocompleteMultipleProps } from '@molecules/form';
import { prepareTalentsOptions } from '@pages/schedule/helpers';
import { Box, Stack, Tooltip } from '@mui/material';
import { FaIcon } from '@atoms/icons';
import { OrganizationAvatar } from '@atoms/dataDisplay';
import { useLocationStore } from '@libs/store/locations';

export function AssigneesSelect({
  localizationSubset,
  name,
  selectedQualifications = [],
  numberOfCopies = 1,
  ...props
}: Omit<FormAutocompleteMultipleProps<string[]>, 'options' | 'name'> & {
  localizationSubset: string;
  name: string;
  numberOfCopies?: number;
  selectedQualifications?: string[];
}) {
  const intl = useIntl();
  const { watch } = useFormContext();
  const { currentLocationID } = useLocationStore();
  const selected = watch(name) as string[] | undefined;
  const selectedNumber = selected?.length ?? 0;
  const { talents } = useShiftDrawerTalents();
  const options = prepareTalentsOptions(talents, selectedQualifications, selected, currentLocationID);
  const talentsById = makeTalentsDictionary(talents);

  return (
    <FormAutocompleteMultiple
      options={options}
      groupBy={(option) => intl.formatMessage({ id: `talents.selector.stuffingType.${option.groupBy ?? ''}` })}
      label={<FormattedMessage id={`${localizationSubset}.talentConfiguration.assignees.label`} />}
      textFieldProps={{
        placeholder:
          selectedNumber === 0
            ? intl.formatMessage({
                id: `${localizationSubset}.talentConfiguration.assignees.placeholder`,
              })
            : undefined,
      }}
      disableCloseOnSelect
      name={name}
      getOptionDisabled={selectedNumber >= numberOfCopies ? () => true : undefined}
      // Trying to fit tags in one line
      limitTags={3}
      renderTags={(selected, getTagProps) =>
        selected.map((s, i) => (
          <Chip
            label={
              <Stack direction="row" spacing={0.5}>
                <Box>{s.label}</Box>
                <Box color="text.secondary">
                  {(talentsById[s.value].qualifications || []).map((q) => q.abbreviation).join(', ')}
                </Box>
              </Stack>
            }
            title={`${s.label} - ${(talentsById[s.value].qualifications || []).map((q) => q.abbreviation).join(', ')}`}
            {...getTagProps({ index: i })}
            key={i}
          />
        ))
      }
      renderOption={(props, option) => (
        <li {...props} key={+new Date() + Math.random()}>
          <Stack direction="row" width="100%" spacing={1}>
            <Box width={'20px'}>
              {(option as any).agency && (
                <Tooltip title={(option as any).agency.name} placement="right">
                  <OrganizationAvatar
                    src={(option as any).agency.logoUrl}
                    fullName={(option as any).agency.name}
                    sx={{ width: '20px', height: '20px' }}
                  />
                </Tooltip>
              )}
            </Box>
            <Box width={'16px'}>
              {(option as any).isOriented && (
                <FaIcon
                  name="square-check"
                  color="#4caf50"
                  variant="solid"
                  style={{
                    fontSize: '14px',
                    backgroundColor: '#fff',
                    width: '16px',
                    height: '16px',
                  }}
                />
              )}
            </Box>
            <Box>{option.label}</Box>
            <Box flex={1} />
            <Typography variant="caption" color="text.secondary">
              {(talentsById[option.value].qualifications || []).map((q) => q.abbreviation).join(', ')}
            </Typography>
          </Stack>
        </li>
      )}
      {...props}
    />
  );
}

function makeTalentsDictionary<T extends { id: string }>(talents: T[]) {
  return talents.reduce<Record<string, T>>((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {});
}
